import React, { useState, useRef, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { makeStyles } from '@material-ui/core/styles';
import { Button, Container, Typography, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Box, TextField } from '@material-ui/core';
import FormatBoldIcon from '@material-ui/icons/FormatBold';
import FormatItalicIcon from '@material-ui/icons/FormatItalic';
import FormatUnderlinedIcon from '@material-ui/icons/FormatUnderlined';
import FormatListNumberedIcon from '@material-ui/icons/FormatListNumbered';
import FormatListBulletedIcon from '@material-ui/icons/FormatListBulleted';
import SaveIcon from '@material-ui/icons/Save';
import { patientActions } from '../../core/actions';
import { useDispatch } from 'react-redux';
import { RADIOLOGYREPORT } from '../../shared/constants';
import { useOrganization } from '../../configurations/OrganizationContext';

const useStyles = makeStyles((theme) => ({
  root: {
    height: '90vh',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(3),
    position: 'relative',
    overflow: 'auto',
  },
  header: {
    marginBottom: theme.spacing(2),
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: '1.5rem',
    color: theme.palette.primary.main,
  },
  formatToolbar: {
    marginBottom: theme.spacing(2),
    border: '1px solid #ddd',
    borderRadius: '4px',
    padding: theme.spacing(1),
    display: 'flex',
    gap: theme.spacing(1),
    backgroundColor: '#fff',
    '& .active': {
      backgroundColor: theme.palette.primary.main,
      color: '#fff',
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
    },
  },
  contentSection: {
    marginBottom: theme.spacing(2),
    '& .ql-container': {
      fontSize: '14px',
      border: 'none',
      '& .ql-editor': {
        minHeight: '60px',
        padding: '8px',
      },
    },
    '& .ql-toolbar': {
      display: 'none',
    },
  },
  table: {
    border: '2px solid #000',
    borderCollapse: 'collapse',
    width: '100%',
    '& th, & td': {
      border: '1px solid #000',
      padding: '8px',
      verticalAlign: 'top',
      fontSize: '14px',
    },
    '& th': {
      backgroundColor: '#f5f5f5',
      fontWeight: 'bold',
      textAlign: 'center',
      fontSize: '0.9rem',
      cursor: 'pointer',
      height: '60px',
    },
    '& td': {
      height: '80px',
      '& .ql-container': {
        border: 'none',
        fontSize: '14px',
        '& .ql-editor': {
          padding: '8px',
          minHeight: '60px',
        },
      },
      '& .ql-toolbar': {
        display: 'none',
      },
    },
    '& td.vessel-name': {
      textAlign: 'left',
      fontWeight: 'bold',
      backgroundColor: '#f9f9f9',
    },
  },
  headerEditField: {
    '& .MuiInputBase-input': {
      fontSize: '0.9rem',
      fontWeight: 'bold',
      textAlign: 'center',
      padding: '8px',
      backgroundColor: '#fff',
    },
  },
  buttonContainer: {
    textAlign: 'center',
    marginTop: theme.spacing(3),
  },
  sectionTitle: {
    marginBottom: theme.spacing(1),
    fontWeight: 'bold',
    color: theme.palette.primary.main,
    fontSize: '1rem',
  },
  doctorInfoContainer: {
    marginTop: theme.spacing(3),
    '& .ql-container': {
      border: 'none',
      fontFamily: theme.typography.fontFamily,
      fontSize: '1rem',
      lineHeight: '1.5',
    },
    '& .ql-editor': {
      // textAlign: 'center',
      padding: theme.spacing(1),
    },
  },
}));

const LowerLimbVenousSystem = (props) => {
  const org = useOrganization();
  const dispatch = useDispatch();
  const classes = useStyles();
  const [editingHeader, setEditingHeader] = useState(null);
  const quillRefs = useRef({});
  const [activeQuill, setActiveQuill] = useState(null);
const [currentFormats, setCurrentFormats] = useState({});

  // Editable headers
  const [headers, setHeaders] = useState([
    { id: 'vessels', text: 'VESSELS' },
    { id: 'linear', text: 'LINEAR' },
    { id: 'compressibility', text: 'COMPRESSIBILITY' },
    { id: 'restingFlow', text: 'RESTING FLOW' },
    { id: 'distalAugmentation', text: 'DISTAL AUGMENTATION' },
  ]);

  // Content above and below table
  const [contentAboveTable, setContentAboveTable] = useState('');
  const [contentBelowTable, setContentBelowTable] = useState('Impression : ');

  // Doctor information
  const [doctorInfo, setDoctorInfo] = useState(
    `<p><strong>${org?.isRadiology?.Doctor || ''}</strong></p>
<p><strong>${org?.isRadiology?.Qualification || ''}</strong></p>
<p><strong>${org?.isRadiology?.RegNo || ''}</strong></p>`
  );

  // Table data with vessel rows
  const [tableData, setTableData] = useState([
    { id: 0, vessels: 'COMMON FEMORAL', linear: '', compressibility: '', restingFlow: '', distalAugmentation: '' },
    { id: 1, vessels: 'SUPERFICIAL FEMORAL', linear: '', compressibility: '', restingFlow: '', distalAugmentation: '' },
    { id: 2, vessels: 'POPLITEAL VEIN', linear: '', compressibility: '', restingFlow: '', distalAugmentation: '' },
    { id: 3, vessels: 'POSTERIOR TIBIAL VEIN', linear: '', compressibility: '', restingFlow: '', distalAugmentation: '' },
    { id: 4, vessels: 'SAPHENOFEMORAL ASJ', linear: '', compressibility: '', restingFlow: '', distalAugmentation: '' },
    { id: 5, vessels: 'LONG SAPHENOUS', linear: '', compressibility: '', restingFlow: '', distalAugmentation: '' },
    { id: 6, vessels: 'SAPHENOPOPLITEAL ASJ', linear: '', compressibility: '', restingFlow: '', distalAugmentation: '' },
  ]);

  // Toolbar modules configuration
  const modules = {
    toolbar: false,
    clipboard: { matchVisual: false }
  };

  const formats = ['bold', 'italic', 'underline', 'list', 'bullet'];

  // Convert to HTML function
  const convertToHTML = () => {
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Lower Limb Venous System Duplex Color Doppler Study</title>
  <style>
    body {
      font-family: Arial, sans-serif;
      line-height: 1.6;
      margin: 20px;
      white-space: pre-wrap;
    }
    .lower-limb-venous-report {
      max-width: 1200px;
      margin: 0 auto;
    }
    h2 {
      text-align: center;
      margin-bottom: 20px;
      font-size: 18px;
    }
    .main-table {
      width: 100%;
      border-collapse: collapse;
      border: 2px solid #000;
      margin: 20px 0;
    }
    .main-table th, .main-table td {
      border: 1px solid #000;
      padding: 8px;
      text-align: center;
    }
    .main-table th {
      background-color: #f5f5f5;
      font-weight: bold;
      font-size: 14px;
    }
    .main-table td.vessel-name {
      text-align: left;
      font-weight: bold;
      background-color: #f9f9f9;
    }
    .content-above-table, .content-below-table {
      margin: 15px 0;
      white-space: pre-wrap;
    }
    .doctor-info {
      text-align: center;
      margin-top: 30px;
      margin-bottom: 20px;
      font-size: 1rem;
      line-height: 1.5;
    }
    .doctor-info p {
      margin-bottom: 8px;
    }
  </style>
</head>
<body>
  <div class="lower-limb-venous-report">
    <h2>LOWER LIMB VENOUS SYSTEM<br>DUPLEX COLOR DOPPLER STUDY OF LOWER LIMB VENOUS SYSTEM</h2>
    
    <div class="content-above-table">
      ${(contentAboveTable || '').replace(/  /g, '&nbsp;&nbsp;')}
    </div>
    
    <table class="main-table">
      <thead>
        <tr>
          ${headers.map(header =>
      `<th>${header.text}</th>`
    ).join('')}
        </tr>
      </thead>
      <tbody>
        ${tableData.map(row =>
      `<tr>
            ${headers.map((header, index) =>
        `<td ${index === 0 ? 'class="vessel-name"' : ''}>${row[header.id] || ''}</td>`
      ).join('')}
          </tr>`
    ).join('')}
      </tbody>
    </table>
    
    <div class="content-below-table">
      ${(contentBelowTable || '').replace(/  /g, '&nbsp;&nbsp;')}
    </div>

    <div class="doctor-info">
      ${doctorInfo.replace(/  /g, '&nbsp;&nbsp;')}
    </div>
  </div>
</body>
</html>`;
  };

  // Parse HTML content function
  const parseHTMLContent = (htmlString) => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlString, 'text/html');

      const defaultVesselNames = [
        'COMMON FEMORAL',
        'SUPERFICIAL FEMORAL',
        'POPLITEAL VEIN',
        'POSTERIOR TIBIAL VEIN',
        'SAPHENOFEMORAL ASJ',
        'LONG SAPHENOUS',
        'SAPHENOPOPLITEAL ASJ'
      ];

      const defaultHeaders = [
        { id: 'vessels', text: 'VESSELS' },
        { id: 'linear', text: 'LINEAR' },
        { id: 'compressibility', text: 'COMPRESSIBILITY' },
        { id: 'restingFlow', text: 'RESTING FLOW' },
        { id: 'distalAugmentation', text: 'DISTAL AUGMENTATION' },
      ];

      const contentAboveElement = doc.querySelector('.content-above-table');
      const contentAbove = contentAboveElement ? contentAboveElement.innerHTML : '';

      const contentBelowElement = doc.querySelector('.content-below-table');
      const contentBelow = contentBelowElement ? contentBelowElement.innerHTML : '';

      const doctorElement = doc.querySelector('.doctor-info');
      const extractedDoctorInfo = doctorElement ? doctorElement.innerHTML :
        `<p><strong>${org?.isRadiology?.Doctor}</strong></p>
<p><strong>${org?.isRadiology?.Qualification}</strong></p>
<p><strong>${org?.isRadiology?.RegNo}</strong></p>`;

      const headerElements = doc.querySelectorAll('thead th');
      const extractedHeaders = headerElements.length > 0
        ? Array.from(headerElements).map((th, index) => ({
          id: defaultHeaders[index]?.id || `col${index}`,
          text: th.textContent.trim() || defaultHeaders[index]?.text || `Column ${index + 1}`
        }))
        : defaultHeaders;

      const bodyRows = doc.querySelectorAll('tbody tr');
      const extractedTableData = bodyRows.length > 0
        ? Array.from(bodyRows).map((row, rowIndex) => {
          const cells = row.querySelectorAll('td');
          const rowData = { id: rowIndex };

          cells.forEach((cell, cellIndex) => {
            const headerId = extractedHeaders[cellIndex]?.id || `col${cellIndex}`;

            if (headerId === 'vessels' && (!cell.innerHTML || cell.innerHTML.trim() === '') && defaultVesselNames[rowIndex]) {
              rowData[headerId] = defaultVesselNames[rowIndex];
            } else {
              rowData[headerId] = cell.innerHTML;
            }
          });

          return rowData;
        })
        : defaultVesselNames.map((vesselName, index) => ({
          id: index,
          vessels: vesselName,
          linear: '',
          compressibility: '',
          restingFlow: '',
          distalAugmentation: ''
        }));

      return {
        headers: extractedHeaders,
        tableData: extractedTableData,
        contentAboveTable: contentAbove,
        contentBelowTable: contentBelow,
        doctorInfo: extractedDoctorInfo
      };
    } catch (error) {
      console.error('Error parsing HTML content:', error);
      return null;
    }
  };

  // Initialize from props
  useEffect(() => {
    console.log("LowerLimbVenous component mounted", props);
    if (props.selectedProcedure) {
      console.log("Selected Procedure:", props.selectedProcedure);
      const selectedProcedure = props.selectedProcedure;

      if (selectedProcedure.longdesc && selectedProcedure.longdesc !== "") {
        const longdesc = selectedProcedure.longdesc;
        console.log("Long Description:", longdesc);

        if (longdesc.trim().startsWith('<') && longdesc.includes('</')) {
          const parsedData = parseHTMLContent(longdesc);
          if (parsedData) {
            setHeaders(parsedData.headers);
            setTableData(parsedData.tableData);
            setContentAboveTable(parsedData.contentAboveTable);
            setContentBelowTable(parsedData.contentBelowTable);
            setDoctorInfo(parsedData.doctorInfo);
          }
        } else {
          try {
            let parsedData = JSON.parse(longdesc);
            console.log("Parsed Data:", parsedData);

            if (parsedData.tableData) {
              setTableData(parsedData.tableData);
            } else if (Array.isArray(parsedData)) {
              setTableData(parsedData);
            }

            if (parsedData.headers) {
              setHeaders(parsedData.headers);
            }

            if (parsedData.contentAboveTable) {
              setContentAboveTable(parsedData.contentAboveTable);
            }

            if (parsedData.contentBelowTable) {
              setContentBelowTable(parsedData.contentBelowTable);
            }

            if (parsedData.doctorInfo) {
              setDoctorInfo(parsedData.doctorInfo);
            }
          } catch (error) {
            console.error("Error parsing longdesc:", error);
          }
        }
      }
    }
    console.log("Initial Table Data:", tableData);
  }, [props.selectedProcedure]);


useEffect(() => {
  if (!activeQuill) return;
  const quill = activeQuill.getEditor();
  
  const updateFormats = () => {
    if (!quill) return;
    const range = quill.getSelection();
    if (range) {
      setCurrentFormats(quill.getFormat(range));
    }
  };

  quill.on('selection-change', updateFormats);
  quill.on('text-change', updateFormats);

  updateFormats(); // Initial call

  // Cleanup listeners on editor or activeQuill change
  return () => {
    quill.off('selection-change', updateFormats);
    quill.off('text-change', updateFormats);
  };
}, [activeQuill]);


  const handleCellChange = (rowIndex, field, value) => {
    const newData = [...tableData];
    newData[rowIndex][field] = value;
    setTableData(newData);
    console.log("handle save change ---", newData);
  };

  const handleHeaderClick = (headerId) => {
    setEditingHeader(headerId);
  };

  const handleHeaderSave = (headerId, newText) => {
    const newHeaders = headers.map(header =>
      header.id === headerId
        ? { ...header, text: newText }
        : header
    );
    setHeaders(newHeaders);
    setEditingHeader(null);
  };

  // Edit procedure function
  const editProcedure = (procedure) => {
    dispatch(
      patientActions.editProcedure(procedure, rescb => {
        console.log("Procedure edited successfully:", rescb);
        if (rescb && rescb.successMessages) {
          console.log("Procedure edited successfully:", rescb);
          props.closeModal({
            success: "LOWER LIMB VENOUS SYSTEM report updated successfully",
          });
        } else {
          console.error("Error editing procedure:", rescb);
        }
      })
    );
  };

  // Save procedure function
  const saveProcedure = (procedure) => {
    let new_procedure = {
      patientId: procedure.patientId,
      visitId: procedure.visitId,
      templateId: "LOWER LIMB VENOUS SYSTEM",
      groupname: "ULTRASOUND",
      subgroup: RADIOLOGYREPORT,
      longdesc: procedure.longdesc,
      notes: "Lower Limb Venous System Duplex Color Doppler Study",
      billNumber: props.selectedProcedure.billNumber,
      markForDel: procedure.markForDel,
      billable: false,
      packageService: props.selectedProcedure?.packageService ? props.selectedProcedure.packageService : null
    };
    if (new_procedure.packageService && new_procedure.billable) {
      new_procedure.packageService.billable = false;
    }
    console.log("New Procedure to be saved:", new_procedure);

    dispatch(
      patientActions.insertToProcedure(new_procedure, rescb => {
        console.log("Procedure added successfully:", rescb);
        if (rescb && rescb.successMessages) {
          console.log("Procedure added successfully:", rescb);
          props.closeModal({
            success: new_procedure.templateId + " report added successfully",
          });
        } else {
          console.error("Error adding procedure:", rescb);
        }
      })
    );
  };

  // Handle save function
  const handleSave = () => {
    const htmlData = convertToHTML();
    console.log('Lower Limb Venous data saved as HTML:', htmlData);

    let updatedProcedure = props.selectedProcedure;
    if (props.selectedProcedure && props.selectedProcedure.longdesc) {
      updatedProcedure.groupname = "ULTRASOUND";
      updatedProcedure.subgroup = RADIOLOGYREPORT;
      console.log("Updating existing procedure with new data");
      updatedProcedure.longdesc = htmlData;
      editProcedure(updatedProcedure);
      props.closeModal({
        success: updatedProcedure.templateId + " report updated successfully",
      });
    } else {
      if (updatedProcedure) {
        console.log("Creating new procedure with data");
        updatedProcedure.longdesc = htmlData;
        updatedProcedure.groupname = "ULTRASOUND";
        console.log("Updated Procedure:", updatedProcedure);
        editProcedure(updatedProcedure);
        saveProcedure(updatedProcedure);
      } else {
        let new_procedure = {
          patientId: props.patientInfo.id,
          visitId: props.patientInfo.visit.id,
          groupname: "ULTRASOUND",
          subgroup: RADIOLOGYREPORT,
          templateId: "LOWER LIMB VENOUS SYSTEM",
          longdesc: htmlData,
          notes: "Lower Limb Venous System Duplex Color Doppler Study Report",
          billNumber: props.selectedProcedure.billNumber,
          markForDel: false,
          billable: false,
        };
        saveProcedure(new_procedure);
      }
    }
  };

  // Quill ref management
  const setQuillRef = (key, ref) => {
    quillRefs.current[key] = ref;
  };

  const handleFocus = (key) => {
    setActiveQuill(quillRefs.current[key]);
  };

  // Toolbar handlers - apply to active editor
  const handleFormat = (format) => {
    if (!activeQuill) return;
    
    const quill = activeQuill.getEditor();
    const range = quill.getSelection();
    
    if (range) {
      if (format === 'bold' || format === 'italic' || format === 'underline') {
        const current = quill.getFormat(range);
        quill.format(format, !current[format]);
      } else if (format === 'list-ordered') {
        quill.format('list', 'ordered');
      } else if (format === 'list-bullet') {
        quill.format('list', 'bullet');
      }
    }
  };

  return (
    <Container maxWidth="lg" className={classes.root}>
      <Typography variant="h4" className={classes.header}>
        LOWER LIMB VENOUS SYSTEM
      </Typography>
      <Typography variant="h5" component="div" style={{ textAlign: 'center', marginBottom: '16px' }}>
        <u>DUPLEX COLOR DOPPLER STUDY OF LOWER LIMB VENOUS SYSTEM</u>
      </Typography>

      {/* Unified Toolbar */}
      <Box className={classes.formatToolbar}>
        <IconButton onClick={() => handleFormat('bold')} aria-label="bold" className={currentFormats.bold ? 'active' : ''}>
          <FormatBoldIcon />
        </IconButton>
        <IconButton onClick={() => handleFormat('italic')} aria-label="italic" className={currentFormats.italic ? 'active' : ''}>
          <FormatItalicIcon />
        </IconButton>
        <IconButton onClick={() => handleFormat('underline')} aria-label="underline" className={currentFormats.underline ? 'active' : ''}>
          <FormatUnderlinedIcon />
        </IconButton>
        <IconButton onClick={() => handleFormat('list-ordered')} aria-label="ordered list" className={currentFormats.list === 'ordered' ? 'active' : ''}>
          <FormatListNumberedIcon />
        </IconButton>
        <IconButton onClick={() => handleFormat('list-bullet')} aria-label="bullet list" className={currentFormats.list === 'bullet' ? 'active' : ''}>
          <FormatListBulletedIcon />
        </IconButton>
      </Box>

      {/* Content Above Table */}
      <Box className={classes.contentSection}>
        <ReactQuill
          ref={ref => setQuillRef('contentAbove', ref)}
          value={contentAboveTable}
          onChange={setContentAboveTable}
          theme="snow"
          modules={modules}
          formats={formats}
          onFocus={() => handleFocus('contentAbove')}
        />
      </Box>

      {/* Main Table */}
      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            {headers.map((header) => (
              <TableCell key={header.id} onClick={() => handleHeaderClick(header.id)}>
                {editingHeader === header.id ? (
                  <TextField
                    defaultValue={header.text}
                    className={classes.headerEditField}
                    onBlur={(e) => handleHeaderSave(header.id, e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleHeaderSave(header.id, e.target.value);
                      }
                    }}
                    autoFocus
                    fullWidth
                    variant="standard"
                  />
                ) : (
                  header.text
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {tableData.map((row, rowIndex) => (
            <TableRow key={row.id}>
              {headers.map((header, headerIndex) => (
                <TableCell key={header.id} className={headerIndex === 0 ? 'vessel-name' : ''}>
                  <ReactQuill
                    ref={ref => setQuillRef(`${rowIndex}-${header.id}`, ref)}
                    value={row[header.id] || ''}
                    onChange={(value) => handleCellChange(rowIndex, header.id, value)}
                    theme="snow"
                    modules={modules}
                    formats={formats}
                    onFocus={() => handleFocus(`${rowIndex}-${header.id}`)}
                  />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Content Below Table */}
      <Box className={classes.contentSection}>
        <ReactQuill
          ref={ref => setQuillRef('contentBelow', ref)}
          value={contentBelowTable}
          onChange={setContentBelowTable}
          theme="snow"
          modules={modules}
          formats={formats}
          onFocus={() => handleFocus('contentBelow')}
        />
      </Box>

      {/* Doctor Information */}
      <Box className={classes.doctorInfoContainer}>
        <ReactQuill
          ref={ref => setQuillRef('doctorInfo', ref)}
          value={doctorInfo}
          onChange={setDoctorInfo}
          theme="snow"
          modules={modules}
          formats={formats}
          onFocus={() => handleFocus('doctorInfo')}
        />
      </Box>

      {/* Action Buttons */}
      <Box className={classes.buttonContainer}>
        <Button
          variant="contained"
          color="primary"
          //startIcon={<SaveIcon />}
          onClick={handleSave}
          size="large"
        >
          Save
        </Button>
      </Box>
    </Container>
  );
};

export default LowerLimbVenousSystem;